import { useEffect, useState } from "react";
import { Ban, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useApi } from "@/lib/useApi";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDateTime, saleUnitLabel, purchaseUnitLabel } from "@/lib/format";
import { ChannelChip, SalesTypeBadge, VoidBadge } from "./Chips";
import { Loading, Num, PrimaryBtn, OutlineBtn } from "./ui";
import Lightbox from "./Lightbox";
import FilterTable from "@/components/import/FilterTable";

const unitFor = (t, l) => (t.type === "SLS" ? saleUnitLabel(l) : t.type === "PUR" ? purchaseUnitLabel(l) : l.stock_unit || "pcs");

const TRACE_COLUMNS = [
  { key: "order_id", label: "Order ID", type: "text", token: (r) => r.order_id || "", cellClass: "num" },
  { key: "product", label: "Produk", type: "text", token: (r) => r.product_name || "", cell: (r) => r.product_name },
  { key: "raw_variation", label: "Variasi", type: "text", token: (r) => r.raw_variation || "", cell: (r) => r.raw_variation || "-" },
  { key: "qty", label: "Qty", type: "num", align: "right", token: (r) => r.qty },
  { key: "ship_time", label: "Kirim", type: "datetime", token: (r) => r.ship_time || "", cell: (r) => r.ship_time || "-" },
  { key: "paid_time", label: "Paid", type: "datetime", token: (r) => r.paid_time || "COD", cell: (r) => (r.paid_time ? r.paid_time : <span className="font-semibold">COD</span>) },
  { key: "source_file", label: "File", type: "text", token: (r) => r.source_file || "", cell: (r) => <span className="block max-w-[140px] truncate" title={r.source_file}>{r.source_file}</span> },
];

function Info({ t }) {
  const rows = [
    ["Tanggal", fmtDate(t.date)], ["PIC", t.pic_name], ["Dibuat oleh", `${t.created_by_name} · ${fmtDateTime(t.created_at)}`],
    t.batch_no && ["Batch", `Batch ${t.batch_no}`], t.subtype === "bake_fd" && ["Jenis", "Panggang dari Frozen Dough"],
    t.reason && ["Alasan", t.reason === "Lainnya" ? `Lainnya — ${t.reason_other}` : t.reason], t.mode && ["Mode", t.mode === "opname" ? "Stock Opname" : "Manual ±"],
    t.supplier && ["Supplier", t.supplier], t.ref_no && ["No. Surat Jalan", t.ref_no], t.customer && ["Customer", t.customer],
    t.source === "import" && ["Import", `${t.order_count} order · ${t.line_count} baris · ${(t.source_files || []).join(", ")}`],
    t.notes && ["Catatan", t.notes], t.status === "void" && ["Void", `${t.void_reason} — ${t.voided_by} · ${fmtDateTime(t.voided_at)}`],
  ].filter(Boolean);
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-2 border-b border-dashed border-zinc-200 pb-1.5">
          <dt className="w-28 shrink-0 text-xs uppercase tracking-wider text-zinc-500">{k}</dt>
          <dd className="min-w-0 break-words font-medium">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function AdminActions({ t, onDone }) {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [edit, setEdit] = useState([]);
  const [busy, setBusy] = useState(false);
  if (t.status !== "active") return null;
  const canCorrect = !(t.type === "SLS" && t.subtype === "import");
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "void") await api.post(`/transactions/${t.id}/void`, { reason });
      else await api.post(`/transactions/${t.id}/correct`, { reason, lines: edit.map((l) => ({ ...l, qty: Number(l.qty) })) });
      toast.success(mode === "void" ? "Transaksi di-void, reversal tercatat di Kartu Stok" : "Koreksi tersimpan");
      setMode(null); setReason(""); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-ink p-4" data-testid="txn-admin-actions">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Aksi Admin · tercatat di audit log</div>
      {!mode && (
        <div className="flex flex-wrap gap-2">
          {canCorrect && <OutlineBtn className="h-10" data-testid="txn-correct-btn" onClick={() => { setMode("correct"); setEdit(t.lines.map((l) => ({ product_id: l.product_id, qty: l.qty, output: l.output }))); }}><PencilLine className="h-4 w-4" /> Koreksi Qty</OutlineBtn>}
          <PrimaryBtn className="h-10" data-testid="txn-void-btn" onClick={() => setMode("void")}><Ban className="h-4 w-4" /> Void Transaksi</PrimaryBtn>
        </div>
      )}
      {mode && (
        <div className="space-y-3">
          {mode === "correct" && t.lines.map((l, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="flex-1 text-sm">{l.product_name}{l.output === "fd" ? " (FD)" : ""}</span>
              <input type="number" value={edit[i]?.qty ?? ""} data-testid={`txn-correct-qty-${i}`}
                onChange={(e) => setEdit(edit.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                className="num h-10 w-24 rounded-md border border-zinc-300 text-center font-bold" />
            </div>
          ))}
          <Textarea placeholder="Alasan (wajib)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="txn-action-reason" />
          <div className="flex gap-2">
            <PrimaryBtn className="h-10" disabled={busy || reason.trim().length < 3} onClick={submit} data-testid="txn-action-confirm">{mode === "void" ? "Konfirmasi Void" : "Simpan Koreksi"}</PrimaryBtn>
            <OutlineBtn className="h-10" onClick={() => setMode(null)} data-testid="txn-action-cancel">Batal</OutlineBtn>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TxnDetailDialog({ txnId, onClose, onChanged }) {
  const { isAdmin } = useAuth();
  const { data, loading, reload } = useApi(txnId ? `/transactions/${txnId}` : null);
  const [lb, setLb] = useState(null);
  useEffect(() => setLb(null), [txnId]);
  const t = data?.txn?.id === txnId ? data.txn : null;
  return (
    <Dialog open={Boolean(txnId)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="txn-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 font-display text-xl">
            <span className="num" data-testid="txn-detail-no">{t?.txn_no || "…"}</span> {t && <VoidBadge txn={t} />}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            {t?.type_label}
            {t?.channel && <ChannelChip channel={t.channel} />}
            {t?.sales_type && <SalesTypeBadge type={t.sales_type} />}
          </DialogDescription>
        </DialogHeader>
        {loading || !t ? <Loading /> : (
          <div className="space-y-6">
            <Info t={t} />
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Item</div>
              <div className="overflow-x-auto rounded-lg border border-zinc-200">
                <table className="dense-table w-full">
                  <thead><tr><th>Produk</th><th className="text-right">Qty</th><th>Satuan</th>{t.type === "ADJ" && t.mode === "opname" && <><th className="text-right">Sistem</th><th className="text-right">Fisik</th></>}</tr></thead>
                  <tbody>
                    {t.lines.map((l, i) => (
                      <tr key={i}>
                        <td>{l.product_name}
                          {l.output === "fd" && <span className="ml-1 rounded bg-zinc-100 px-1.5 text-[10px] font-semibold">→ Frozen Dough</span>}
                          {l.output_product_name && <span className="ml-1 text-zinc-500">→ <span className="font-semibold text-ink">{l.output_product_name}</span></span>}
                        </td>
                        <td className="num text-right font-bold">{l.qty}</td><td>{unitFor(t, l)}</td>
                        {t.type === "ADJ" && t.mode === "opname" && <><td className="num text-right">{l.system_qty}</td><td className="num text-right">{l.physical_qty}</td></>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {t.photos?.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Foto Bukti</div>
                <div className="flex flex-wrap gap-2">
                  {t.photos.map((p, i) => (
                    <button key={p.id} onClick={() => setLb(i)} className="press h-20 w-20 overflow-hidden rounded-md border-2 border-ink" data-testid={`txn-photo-${i}`}>
                      <img src={fileUrl(p.id)} alt="bukti" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Pergerakan Stok (Ledger)</div>
              <div className="overflow-x-auto rounded-lg border border-zinc-200">
                <table className="dense-table w-full">
                  <thead><tr><th>Produk</th><th className="text-right">Qty</th><th>Keterangan</th></tr></thead>
                  <tbody>{data.movements.map((m, idx) => (
                    <tr key={`${idx}-${m.product_id}`} className={m.is_reversal ? "italic text-zinc-500" : ""}><td>{m.product_name}</td><td className="text-right"><Num value={m.qty_signed} /></td><td className="max-w-xs truncate">{m.keterangan}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
            {data.sales_lines?.length > 0 && (
              <details className="rounded-lg border border-zinc-200 p-3" data-testid="txn-sales-lines">
                <summary className="cursor-pointer text-sm font-semibold">Detail {data.sales_lines.length} baris order (trace)</summary>
                <div className="mt-3">
                  <FilterTable testid="trace-table" columns={TRACE_COLUMNS} rows={data.sales_lines} />
                </div>
              </details>
            )}
            {data.audit?.length > 0 && (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Audit Log</div>
                <ul className="space-y-1 text-xs" data-testid="txn-audit-log">
                  {data.audit.map((a) => (
                    <li key={a.id} className="flex gap-2"><span className="num text-zinc-400">{fmtDateTime(a.at)}</span><span className="font-semibold uppercase">{a.action}</span><span>{a.user_name}</span>{a.reason && <span className="text-zinc-600">— {a.reason}</span>}</li>
                  ))}
                </ul>
              </div>
            )}
            {isAdmin && <AdminActions t={t} onDone={() => { reload(); onChanged?.(); }} />}
          </div>
        )}
        <Lightbox photos={t?.photos} index={lb} onIndex={setLb} onClose={() => setLb(null)} />
      </DialogContent>
    </Dialog>
  );
}
